// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Alert, Button, Group, Loader, LoadingOverlay, Paper, Stack, Stepper, Title } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { normalizeErrorString } from '@medplum/core';
import type {
  Appointment,
  Encounter,
  Patient,
  Practitioner,
  Questionnaire,
  QuestionnaireResponse,
} from '@medplum/fhirtypes';
import { QuestionnaireForm, useMedplum, useMedplumProfile } from '@medplum/react';
import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { SleepHistoryIntakeForm } from '../../components/encounter/SleepHistoryIntakeForm';
import { type IntakePhase, submitNpIntakeStep } from '../../services/np-intake-submit';
import { isIntakeEncounter } from '../../utils/is-intake-encounter';

const TAG_SYSTEM = 'http://polarissleep.com/tags';
const BOOKED_CODE = 'eligibility-appointment-booked';
const PENDING_CODE = 'eligibility-appointment-pending';
const FINISHED_CODE = 'eligibility-check-finished';

interface StepDef {
  phase: IntakePhase;
  label: string;
}

const STEPS: readonly StepDef[] = [
  { phase: 'eligibility-check-page-1', label: 'Eligibility 1' },
  { phase: 'sleep-history', label: 'Sleep history' },
] as const;

/**
 * Maps the patient's current Polaris tag to a zero-based step index in the
 * four-questionnaire NP intake flow. Returns `STEPS.length` once finished, so
 * the page can navigate away.
 */
function computeActiveStep(patient: Patient | undefined, encounter?: Encounter, appointment?: Appointment): number {
  if (!patient) {
    return 0;
  }
  const codes = patient.meta?.tag?.filter((t) => t.system === TAG_SYSTEM).map((t) => t.code) ?? [];
  if (codes.includes(FINISHED_CODE)) {
    return STEPS.length;
  }
  // Map each phase tag to its step. The booked tag and the page-1 tag both
  // sit at step 0 (booked = haven't started; page-1 = patch happened but bot
  // call hadn't advanced yet, so retry from the same step).
  for (let i = STEPS.length - 1; i >= 0; i -= 1) {
    if (codes.includes(STEPS[i].phase)) {
      return i;
    }
  }
  if (codes.includes(BOOKED_CODE)) {
    return 0;
  }
  if (codes.includes('sleep-history') || codes.includes('stop-bang') || codes.includes('epworth-sleepiness-scale')) {
    return 1;
  }
  if (isIntakeEncounter(encounter, appointment)) {
    return 0;
  }
  return -1;
}

function needsFirstStepTransition(patient: Patient | undefined): boolean {
  return getFirstStepTag(patient) !== undefined;
}

function getFirstStepTag(
  patient: Patient | undefined
): 'eligibility-appointment-booked' | 'eligibility-appointment-pending' | undefined {
  const code = patient?.meta?.tag?.find((tag) => tag.system === TAG_SYSTEM)?.code;
  return code === BOOKED_CODE || code === PENDING_CODE ? code : undefined;
}

export function NpIntakeFormPage(): JSX.Element {
  const { patientId, encounterId } = useParams();
  const navigate = useNavigate();
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | undefined;

  const [patient, setPatient] = useState<Patient | undefined>();
  const [encounter, setEncounter] = useState<Encounter | undefined>();
  const [appointment, setAppointment] = useState<Appointment | undefined>();
  const [questionnairesByPhase, setQuestionnairesByPhase] = useState<Record<string, Questionnaire>>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);

  const activeStep = useMemo(
    () => computeActiveStep(patient, encounter, appointment),
    [patient, encounter, appointment]
  );

  useEffect(() => {
    if (!patientId || !encounterId) {
      setLoadError('Missing patient or encounter id in URL.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(undefined);
    Promise.all([
      medplum.readResource('Patient', patientId),
      medplum.readResource('Encounter', encounterId),
      medplum.searchResources('Questionnaire', { name: STEPS.map((s) => s.phase).join(',') }),
      medplum.searchResources('Questionnaire', { name: 'stop-bang,epworth-sleepiness-scale' }),
    ])
      .then(async ([loadedPatient, loadedEncounter, loadedQuestionnaires, sleepQuestionnaires]) => {
        if (cancelled) {
          return;
        }
        const appointmentReference = loadedEncounter.appointment?.at(-1)?.reference;
        const appointmentId = appointmentReference?.startsWith('Appointment/')
          ? appointmentReference.split('/')[1]
          : undefined;
        const loadedAppointment = appointmentId
          ? await medplum.readResource('Appointment', appointmentId).catch(() => undefined)
          : undefined;
        const byPhase: Record<string, Questionnaire> = {};
        for (const q of [...loadedQuestionnaires, ...sleepQuestionnaires]) {
          if (q.name) {
            byPhase[q.name] = q;
          }
        }
        setPatient(loadedPatient);
        setEncounter(loadedEncounter);
        setAppointment(loadedAppointment);
        setQuestionnairesByPhase(byPhase);
      })
      .catch((err) => {
        if (cancelled) {
          return;
        }
        setLoadError(normalizeErrorString(err));
      })
      .finally(() => {
        if (cancelled) {
          return;
        }
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [medplum, patientId, encounterId]);

  const backToEncounter = (): void => {
    navigate(`/Patient/${patientId}/Encounter/${encounterId}`);
  };

  const handleSubmit = async (
    response: QuestionnaireResponse,
    epworthResponse?: QuestionnaireResponse,
    stopBangResponse?: QuestionnaireResponse
  ): Promise<void> => {
    // Defense in depth: QuestionnaireForm's submit button doesn't lock during
    // an async onSubmit, so a fast double-click can fire two requests. The
    // second would arrive after the bot has already advanced the patient's
    // tag, tripping the phase-mismatch guard. Refuse the second call.
    if (submitting) {
      return;
    }
    if (!patient || !encounter || !profile || activeStep < 0 || activeStep >= STEPS.length) {
      return;
    }
    const step = STEPS[activeStep];
    const isFirstStep = needsFirstStepTransition(patient);
    try {
      setSubmitting(true);
      await submitNpIntakeStep({
        medplum,
        patient,
        encounter,
        practitioner: profile,
        phase: step.phase,
        response,
        isFirstStep,
        epworthResponse,
        stopBangResponse,
      });
      // Refresh patient to read the new tag the bot wrote. cache: 'no-cache'
      // is critical: the bot advanced Patient.meta.tag on the server, but the
      // Medplum client's request cache would otherwise return the pre-submit
      // version, leaving activeStep stuck at the previous step. Re-fetching
      // forces the next render to recompute the stepper position.
      const refreshed = await medplum.readResource('Patient', patient.id ?? '', { cache: 'no-cache' });
      setPatient(refreshed);
      if (computeActiveStep(refreshed, encounter, appointment) >= STEPS.length) {
        showNotification({ color: 'green', message: 'Intake completed.' });
        backToEncounter();
      } else {
        showNotification({ color: 'green', message: `${step.label} saved.`, autoClose: 1500 });
      }
    } catch (err) {
      showNotification({ color: 'red', message: `Failed to submit step: ${normalizeErrorString(err)}` });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Stack p="md" align="center">
        <Loader />
      </Stack>
    );
  }

  if (loadError) {
    return (
      <Stack p="md" gap="md">
        <Title order={2}>Intake Form</Title>
        <Alert color="red" title="Could not load intake form">
          {loadError}
        </Alert>
        <Group>
          <Button variant="default" onClick={backToEncounter}>
            Back to encounter
          </Button>
        </Group>
      </Stack>
    );
  }

  if (activeStep < 0) {
    return (
      <Stack p="md" gap="md">
        <Title order={2}>Intake Form</Title>
        <Alert color="yellow" title="Not on the scheduled-intake path">
          This patient has not booked a scheduled intake appointment. The intake form completion path is only available
          for patients who chose to schedule an intake appointment.
        </Alert>
        <Group>
          <Button variant="default" onClick={backToEncounter}>
            Back to encounter
          </Button>
        </Group>
      </Stack>
    );
  }

  if (activeStep >= STEPS.length) {
    // Already finished; bounce back. Don't render the form for a completed
    // patient — the chart's view-only screener card is the right surface.
    return (
      <Stack p="md" gap="md">
        <Title order={2}>Intake Form</Title>
        <Alert color="green" title="Intake already completed">
          The intake form for this patient is already complete.
        </Alert>
        <Group>
          <Button variant="default" onClick={backToEncounter}>
            Back to encounter
          </Button>
        </Group>
      </Stack>
    );
  }

  const currentPhase = STEPS[activeStep].phase;
  const currentQuestionnaire = questionnairesByPhase[currentPhase];

  return (
    <Stack p="md" gap="md">
      <Title order={2}>Intake Form</Title>
      <Stepper active={activeStep} allowNextStepsSelect={false}>
        {STEPS.map((step) => (
          <Stepper.Step key={step.phase} label={step.label} />
        ))}
      </Stepper>
      <Paper withBorder p="md" pos="relative">
        <LoadingOverlay visible={submitting} overlayProps={{ blur: 1 }} loaderProps={{ children: 'Submitting…' }} />
        {currentPhase === 'sleep-history' && (
          <SleepHistoryIntakeForm
            submitting={submitting}
            onSubmit={(epworthResponse, stopBangResponse) =>
              handleSubmit(
                { resourceType: 'QuestionnaireResponse', status: 'completed' },
                epworthResponse,
                stopBangResponse
              )
            }
          />
        )}
        {currentPhase !== 'sleep-history' && currentQuestionnaire && (
          <QuestionnaireForm
            key={currentPhase}
            questionnaire={currentQuestionnaire}
            onSubmit={handleSubmit}
            disablePagination
          />
        )}
        {currentPhase !== 'sleep-history' && !currentQuestionnaire && (
          <Alert color="red" title="Questionnaire not found">
            Could not load the questionnaire for phase <strong>{currentPhase}</strong>. Confirm it is provisioned in the
            Medplum project.
          </Alert>
        )}
      </Paper>
      <Group>
        <Button variant="default" onClick={backToEncounter} disabled={submitting}>
          Cancel
        </Button>
      </Group>
    </Stack>
  );
}
